import { Link } from 'react-router-dom';

import { formatCount, formatDelta } from './format';

/**
 * One KPI tile — CLM-81 (D-81-1). Hand-rolled, tokens only: the tone maps onto
 * the existing `--ok` / `--bad` / `--ai` / `--acc` / `--up` pairs through
 * `analytics.css`, so CLM-73 re-skins it by editing tokens. No colour literal
 * lives here.
 *
 * `n_today` is optional: the Overview tiles carry today's intake as a footer
 * chip (`tag ok` when positive, plain `tag` at zero); the Dashboard tiles have
 * no today line — their KPIs are point-in-time (D-81-14).
 *
 * CLM-90 (D-90-12) — two additive props: `to` wraps the title in a `<Link>`
 * (every mount of the tile already sits inside a router) and `caption` adds one
 * `.kpi-caption` line under the unit. Existing call sites are untouched.
 *
 * CLM-95 (D-95-3) — a third, mutually exclusive mode: `expanded`/`onToggle`
 * (with `panelId`) renders a `<button aria-expanded aria-controls>` in place
 * of the title, for tiles that open an in-place disclosure instead of
 * navigating. Passing both `to` and `onToggle` is a caller error (a tile is
 * either a link or a toggle, never both) — enforced by the prop union below,
 * not a runtime check.
 */

export type TileTone = 'ok' | 'bad' | 'ai' | 'acc' | 'up';

interface StatTileBaseProps {
  readonly title: string;
  readonly n: number;
  readonly n_today?: number | undefined;
  readonly unit?: string | undefined;
  readonly tone?: TileTone | undefined;
  readonly caption?: string | undefined;
}

type StatTileProps =
  | (StatTileBaseProps & { readonly to?: string | undefined; readonly onToggle?: undefined })
  | (StatTileBaseProps & {
      readonly to?: undefined;
      readonly onToggle: () => void;
      readonly expanded: boolean;
      readonly panelId: string;
      /** A stable id the disclosure section's own collapse control focuses back to (D-95-3). */
      readonly id: string;
    });

export function StatTile(props: StatTileProps) {
  const { title, n, n_today, unit = 'contracts', tone, caption } = props;
  return (
    <article className="card kpi" data-tone={tone} aria-label={title}>
      <h3>
        {props.onToggle === undefined ? (
          props.to === undefined ? (
            title
          ) : (
            <Link to={props.to}>{title}</Link>
          )
        ) : (
          <button
            type="button"
            id={props.id}
            className="kpi-toggle"
            aria-expanded={props.expanded}
            aria-controls={props.panelId}
            onClick={props.onToggle}
          >
            {title}
          </button>
        )}
      </h3>
      <p className="kpi-n">{formatCount(n)}</p>
      <p className="kpi-unit">{unit}</p>
      {caption === undefined ? null : <p className="kpi-caption">{caption}</p>}
      {n_today === undefined ? null : (
        <footer>
          <span className={n_today > 0 ? 'tag ok' : 'tag'}>{formatDelta(n_today)} today</span>
        </footer>
      )}
    </article>
  );
}
